import * as React from "react";
import { history } from "../../../_Bootstrap/RoutingAndStore";
import PrimaryButton from "../../../Common/Buttons/PrimaryButton";
import NiceError from "../../../Common/Errors/NiceError";
import { getOption } from "../Options/Options";

export type SubscriptionName = "basic" | "standard" | "premium" | "ice";
export type AddOnName = "kitchen" | "banner" | "liquor";
export type SubscriptionAddOn = `addon:${AddOnName}`;

// Don't forget to update PHP (SubscriptionFeature.php)!
const features = {
    advanced_stocktake     : [ "standard", "premium" ], // Variant reports, live while trading
    account_customers      : [ "standard", "premium" ], // Putting sales on Account, running statements
    price_lists            : [ "standard", "premium" ], // Setup and using price lists
    // Advanced Loyalty (being able to assign to specific categories)
    advanced_loyalty       : [ "standard", "premium" ],
    gift_cards             : [ "standard", "premium" ], // Able to sell and redeem gift cards
    advanced_permissions   : [ "standard", "premium" ], // Able to customise the roles and permissions
    reporting_dashboard    : [ "standard", "premium" ], // Able to view the reporting dashboard
    // Whether they have a predefined user dashboard or a customisable one
    user_dashboard         : [ "standard", "premium" ],
    // Whether stores can have multiple price sets or just the default set
    price_sets             : [ "premium" ],
    page_rules             : [ "standard", "premium" ], // Whether the Vendor can customise their page rules
    future_prices          : [ "standard", "premium" ], // Whether the Vendor can have price changes occur in the future
    advanced_reports       : [ "premium" ], // Can run advanced reports through the UI
    api                    : [ "premium" ], // Can connect custom applications to the API
    // Can receive invoices from other Vendors using a Vendor Connection (all can send)
    receive_vendor_invoices: [ "premium" ],
    // Can view and use the security centre (temporarily available to everyone)
    security_centre        : [ "basic", "standard", "premium" ],
    // Can set and use public holiday surcharging
    holiday_surcharging    : [ "standard", "premium" ],
    popular_reports        : [ "premium" ], // Can see popular reports
    custom_buying_periods  : [ "premium" ], // Whether buying periods can be created by the user
    create_order_reviews   : [ "premium" ], // Whether order reviews can be created automatically or manually
    future_costs           : [ "standard", "premium" ], // Whether the Vendor can have cost changes occur in the future
    kitchen_display        : [ "addon:kitchen" ], // Whether kitchen displays can be used
    recurring_promotions   : [ "standard", "premium" ], // Whether promotions can be scheduled to reoccur
} satisfies Record<string, Array<SubscriptionName | SubscriptionAddOn>>;

type Feature = keyof typeof features;

export class Subscription {
    public static Features = {
        ADVANCED_STOCKTAKE     : "advanced_stocktake",
        ACCOUNT_CUSTOMERS      : "account_customers",
        PRICE_LISTS            : "price_lists",
        ADVANCED_LOYALTY       : "advanced_loyalty",
        GIFT_CARDS             : "gift_cards",
        ADVANCED_PERMISSIONS   : "advanced_permissions",
        REPORTING_DASHBOARD    : "reporting_dashboard",
        USER_DASHBOARD         : "user_dashboard",
        PRICE_SETS             : "price_sets",
        PAGE_RULES             : "page_rules",
        FUTURE_PRICES          : "future_prices",
        ADVANCED_REPORTS       : "advanced_reports",
        API                    : "api",
        RECEIVE_VENDOR_INVOICES: "receive_vendor_invoices",
        HOLIDAY_SURCHARGING    : "holiday_surcharging",
        POPULAR_REPORTS        : "popular_reports",
        CUSTOM_BUYING_PERIODS  : "custom_buying_periods",
        CREATE_ORDER_REVIEWS   : "create_order_reviews",
        FUTURE_COSTS           : "future_costs",
        KITCHEN_DISPLAY        : "kitchen_display",
        RECURRING_PROMOTIONS   : "recurring_promotions",
    } satisfies Record<string, Feature>;

    public static getSubscription(): SubscriptionName {
        return getOption("subscription_name");
    }

    public static getAddOns(): Array<AddOnName> {
        return getOption("subscription_addons");
    }

    protected static getRawFeature(feature: Feature): Array<SubscriptionName | SubscriptionAddOn> {
        return features[feature];
    }

    /**
     * Checks whether the current subscript has access to provided features, returns the name of the required
     * subscription or null if the features is currently accessible
     * @param feature
     */
    public static getRequiredSubscription(
        feature: Array<Feature> | Feature
    ): null | SubscriptionName | SubscriptionAddOn {
        if(!Array.isArray(feature)) {
            feature = [ feature ];
        }

        const subscription = Subscription.getSubscription();
        const addOns = Subscription.getAddOns();

        for(let i = 0, l = feature.length; i < l; i++) {
            const rawFeature = Subscription.getRawFeature(feature[i]);

            if(rawFeature.includes(subscription)) { // Has subscription
                continue;
            }

            // Check if it requires an add on
            let hasAddOn = false;

            for(let j = 0, n = rawFeature.length; j < n; j++) {
                if(rawFeature[j].startsWith("addon:")) {
                    if(addOns.includes(rawFeature[j].slice(6) as AddOnName)) {
                        hasAddOn = true;
                        break;
                    }
                }
            }

            if(!hasAddOn) {
                return rawFeature[0]; // The first key is the lowest plan / add on
            }
        }

        return null;
    }

    /**
     * Return whether the current subscript has access to the provided features
     * @param feature
     */
    public static can(feature: Array<Feature> | Feature): boolean {
        return Subscription.getRequiredSubscription(feature) === null;
    }
}

export function withSubscription<P>(
    WrappedComponent: React.ComponentType<P>,
    feature: Array<Feature> | Feature
): React.ComponentType<P> {
    return class extends React.Component<P> {
        public static displayName = "WithSubscription";

        public handleClick = (): void => {
            history.push("/settings/account");
        };

        public render(): React.ReactNode {
            const requiredSubscriptionName = Subscription.getRequiredSubscription(feature);

            if(requiredSubscriptionName === null) {
                return (
                    <WrappedComponent
                        {...this.props}
                    />
                );
            }

            return (
                <div className="vertical-center">
                    <NiceError
                        icon="arrow-circle-up"
                        title="Subscription Upgrade Required"
                        actions={[
                            <PrimaryButton
                                key="account-billing-page"
                                onClick={this.handleClick}
                            >
                                Open Account & Billing Page
                            </PrimaryButton>,
                        ]}
                    >
                        <p>This feature isn&#39;t available on your current subscription.</p>
                        <p>
                            You need to be on the &#34;{requiredSubscriptionName}&#34; plan,
                            you&#39;re current on the &#34;{Subscription.getSubscription()}&#34; plan.
                        </p>
                    </NiceError>
                </div>
            );
        }
    };
}
